import { inject, Injectable, InjectionToken, type Signal,signal } from '@angular/core';

/** The two moments of the chat that have a sound. */
export type LeapBotSoundKind = 'sent' | 'received';

/** The slice of an audio parameter the sounds use. */
export interface LeapBotAudioParam {
  setValueAtTime(value: number, time: number): unknown;
  exponentialRampToValueAtTime(value: number, time: number): unknown;
}

/** The slice of an oscillator the sounds use. */
export interface LeapBotOscillator {
  type: string;
  frequency: LeapBotAudioParam;
  connect(destination: unknown): unknown;
  start(time: number): void;
  stop(time: number): void;
}

/** The slice of a gain node the sounds use. */
export interface LeapBotGain {
  gain: LeapBotAudioParam;
  connect(destination: unknown): unknown;
}

/** The slice of the browser audio context the sounds use; tests inject a fake. */
export interface LeapBotAudioContext {
  readonly state: string;
  readonly currentTime: number;
  readonly destination: unknown;
  resume(): Promise<void>;
  createOscillator(): LeapBotOscillator;
  createGain(): LeapBotGain;
}

/** Creates the audio context, or `null` when the browser has no Web Audio support. */
export const LEAPBOT_AUDIO_CONTEXT = new InjectionToken<() => LeapBotAudioContext | null>('LEAPBOT_AUDIO_CONTEXT', {
  providedIn: 'root',
  factory: (): (() => LeapBotAudioContext | null) => () => {
    const Context = typeof AudioContext !== 'undefined' ? AudioContext : null;
    return Context ? new Context() : null;
  },
});

/** Browser key holding the user's choice: `on` or `off`. */
export const LEAPBOT_SOUND_STORAGE_KEY = 'fury.leapbot.sound';

/** One note of a sound: frequency in Hz, start offset and length in seconds. */
interface Note {
  frequency: number;
  at: number;
  duration: number;
}

/** Quick rising pair when a message is sent; soft falling pair when LeapBot starts to answer. */
const SOUNDS: Readonly<Record<LeapBotSoundKind, readonly Note[]>> = {
  sent: [
    { frequency: 660, at: 0, duration: 0.09 },
    { frequency: 880, at: 0.07, duration: 0.11 },
  ],
  received: [
    { frequency: 784, at: 0, duration: 0.13 },
    { frequency: 587, at: 0.1, duration: 0.2 },
  ],
};

/** Peak loudness of every note; low on purpose so the sounds are discreet. */
const PEAK_GAIN = 0.07;
const SILENCE = 0.0001;

/**
 * Plays the short chat sounds, generated with the Web Audio API (no files). It is silent when the
 * user muted it or the browser cannot play audio, and it never throws: a sound is a nicety, so a
 * failure must not affect the chat. The choice is remembered in the browser.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotSoundService {
  private readonly createContext = inject(LEAPBOT_AUDIO_CONTEXT);
  private context: LeapBotAudioContext | null = null;
  private readonly enabledState = signal<boolean>(readStoredChoice());

  /** Whether the sounds are on (they are on the first time). */
  readonly enabled: Signal<boolean> = this.enabledState.asReadonly();

  /** Mutes the sounds if they are on and unmutes them if they are muted, remembering the choice. */
  toggle(): void {
    const next = !this.enabledState();
    this.enabledState.set(next);
    try {
      localStorage.setItem(LEAPBOT_SOUND_STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      // Storage blocked: the choice only lasts until the page closes.
    }
  }

  /** Plays a sound unless muted or unavailable. */
  play(kind: LeapBotSoundKind): void {
    if (!this.enabledState()) return;
    try {
      this.context ??= this.createContext();
      const context = this.context;
      if (!context) return;
      // Browsers start the context suspended until the user interacts; the send sound runs inside that interaction.
      if (context.state === 'suspended') void context.resume().catch(() => undefined);
      const start = context.currentTime;
      for (const note of SOUNDS[kind]) this.playNote(context, note, start);
    } catch {
      // Audio is optional: ignore any failure.
    }
  }

  private playNote(context: LeapBotAudioContext, note: Note, start: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const from = start + note.at;
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(note.frequency, from);
    gain.gain.setValueAtTime(SILENCE, from);
    gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, from + 0.012);
    gain.gain.exponentialRampToValueAtTime(SILENCE, from + note.duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(from);
    oscillator.stop(from + note.duration + 0.02);
  }
}

function readStoredChoice(): boolean {
  try {
    return localStorage.getItem(LEAPBOT_SOUND_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}
